import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "@mui/material/Button";
import { FilterMatchMode } from "primereact/api";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import {
  DataTable,
  DataTableFilterMeta,
  DataTableMultiSortMetaType,
  DataTableSortOrderType,
  DataTableSortParams,
} from "primereact/datatable";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AccessControlContext } from "../contexts/AccessControlContext";
import { LOCALSTORAGE_KEYS } from "../shared/constants";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import { DateFilter } from "../shared/DateFilter";
import { RAGStatus } from "../shared/enums/Result";
import { DateTemplate } from "../shared/Grid/BodyTemplates";
import { GlobalFilter } from "../shared/Grid/GlobalFilterTemplate";
import { getSubstationDisplayName } from "../shared/helpers/ResultUtils";
import { Result } from "../shared/interfaces/Result";
import { RAGStatusFilterDropdown } from "../shared/RAGStatusFilterDropdown";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import { ActionBodyTemplate } from "./ActionBodyTemplate";
import RAGStatusChip from "./RAGStatusChip";
import { ResultsGridAssetCellTemplate } from "./ResultsGridAssetCellTemplate";

interface Props {
  resultData?: Result[];
  resultDataLoading: boolean;
  resultDataFetching: boolean;
  partialResults: boolean;
  customerId: number;
  companyId: number;
  siteId: number;
  onDataRefreshClick: () => void;
  onUploadButtonClick: () => void;
}

const sortFieldStorageKey = LOCALSTORAGE_KEYS.resultsSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.resultsSortOrder;

export function ResultsGrid(props: Props) {
  const { t } = useTranslation();

  const accessControlContext = useContext(AccessControlContext);
  const userCanUploadResults = accessControlContext.canI("add", "results", props.customerId);

  const [searchParams] = useSearchParams();
  const filters = useMemo<DataTableFilterMeta>(() => {
    const substation = searchParams.get("substation");
    return {
      "dataValue.OverallRAGStatus": {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
      },
      time: { value: null, matchMode: FilterMatchMode.DATE_IS },
      substationDisplayName: {
        value: substation,
        matchMode: FilterMatchMode.CONTAINS,
      },
      switchGearName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };
  }, [searchParams]);

  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const onSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  useEffect(() => {
    const field = (localStorage.getItem(sortFieldStorageKey) as string) ?? "time";
    const order = (localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType) ?? -1;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  }, [props]);

  const tableData = useMemo(
    () =>
      props.resultData?.map((result) => {
        return {
          ...result,
          OverallRAGStatusIndex: Object.values(RAGStatus).indexOf(result.dataValue.OverallRAGStatus ?? RAGStatus.None),
          time: new Date(result.time),
          substationDisplayName: getSubstationDisplayName(result),
        };
      }),
    [props.resultData],
  );

  function handleGlobalFilterInput(event: React.FormEvent<HTMLInputElement>): void {
    const element = event.target as HTMLInputElement;

    setGlobalFilter(element.value);
  }

  const headerToolbar = (
    <Row>
      <Col>
        {userCanUploadResults && (
          <Button
            variant="contained"
            color="primary"
            onClick={props.onUploadButtonClick}
            startIcon={<FontAwesomeIcon style={{ cursor: "pointer" }} icon={faUpload} />}
          >
            {t("results.submitResult")}
          </Button>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        {props.resultData && props.resultData.length > 0 && <GlobalFilter onInput={handleGlobalFilterInput} />}
      </Col>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={props.resultDataFetching} onClick={props.onDataRefreshClick} />
      </Col>
    </Row>
  );

  const ragStatusCellTemplate = (rowData: Result) => {
    const rowRagStatus = rowData.dataValue.OverallRAGStatus ?? RAGStatus.None;
    return <RAGStatusChip ragStatus={rowRagStatus} />;
  };

  const ragStatusFilterDropdown = (options: ColumnFilterElementTemplateOptions) => (
    <RAGStatusFilterDropdown options={options} />
  );

  const dateFilter = (options: ColumnFilterElementTemplateOptions) => (
    <DateFilter options={options} placeholder={t("common.filter", { val: "$t(results.resultDate )" })} />
  );

  if (props.resultDataLoading) return <DataTableSkeleton />;
  else
    return (
      <div className="datatable-responsive">
        <DataTable
          value={tableData}
          header={headerToolbar}
          // If the global filter is an empty string add a space, otherwise some results may get filtered out
          globalFilter={globalFilter === "" ? " " : globalFilter}
          filters={filters}
          filterDisplay="row"
          removableSort
          sortMode="multiple"
          className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
          emptyMessage={t("common.emptyMessage", {
            val: "$t(results.results )",
          })}
          loading={props.resultDataLoading}
          multiSortMeta={multiSortMeta}
          onSort={onSort}
        >
          <Column
            field="dataValue.OverallRAGStatus"
            body={ragStatusCellTemplate}
            headerStyle={{ width: "10em", textAlign: "center" }}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("results.overallStatus")}
            filter
            sortable
            filterElement={ragStatusFilterDropdown}
            filterMatchMode={FilterMatchMode.EQUALS}
            showFilterMenu={false}
            showClearButton={false}
            sortField="OverallRAGStatusIndex"
          />
          <Column
            field={"time"}
            header={t("results.resultDate")}
            body={(rowData: Result) => (
              <DateTemplate rowData={rowData} field={"time"} header={t("results.resultDate")} />
            )}
            filter={props.resultData && props.resultData.length > 0}
            dataType="date"
            filterElement={dateFilter}
            showFilterMenu={false}
            showAddButton={false}
            sortable
            sortField="time"
          />
          <Column
            header={t("substations.substation")}
            field="substationDisplayName"
            filter
            filterMatchMode={FilterMatchMode.CONTAINS}
            filterPlaceholder="Substation Filter"
            showFilterMenu={false}
            showClearButton={false}
            sortable
            sortField="substationDisplayName"
          />
          {!props.partialResults && (
            <Column
              header={t("asset.assets")}
              field="switchGearName"
              body={ResultsGridAssetCellTemplate}
              filter
              filterMatchMode={FilterMatchMode.CONTAINS}
              filterPlaceholder={t("asset.assetFilter")}
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="switchGearName"
            />
          )}
          <Column
            body={(rowData: Result) => (
              <ActionBodyTemplate
                rowData={rowData}
                customerId={props.customerId}
                companyId={props.companyId}
                siteId={props.siteId}
              />
            )}
            headerStyle={{ width: "8em", textAlign: "center" }}
            bodyStyle={{ textAlign: "center", overflow: "visible" }}
            header={t("common.actions")}
          />
        </DataTable>
      </div>
    );
}
